<script setup lang="ts">
import { computed, inject } from "vue";

import type { ThemeContext, ThemeMode } from "@core/composables/ui/useTheme";
import IconAuto from "@core/icons/IconAuto.vue";
import IconMoon from "@core/icons/IconMoon.vue";
import IconSun from "@core/icons/IconSun.vue";

import { useTranslate } from "@common/composables/useTranslate";

const theme = inject<ThemeContext>("theme");

if (!theme) {
  throw new Error("ThemeToggle must be used within ThemeProvider");
}

interface Props {
  layout?: "compact" | "sidebar";
}

const props = withDefaults(defineProps<Props>(), {
  layout: "compact"
});

const translate = useTranslate();

const modes: ThemeMode[] = ["light", "auto", "dark"];

const isSidebar = computed(() => props.layout === "sidebar");
const containerClass = computed(() =>
  isSidebar.value
    ? "flex w-full flex-col gap-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white/90 dark:bg-slate-800/90 p-2 transition-colors duration-300"
    : "flex items-center gap-1 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-1 transition-colors duration-300"
);

const currentModeIndex = computed(() => {
  const index = modes.indexOf(theme.preference.value);
  return index >= 0 ? index : 1; // Default to 'auto'
});

const currentMode = computed(() => modes[currentModeIndex.value]);

const modeLabels = computed<Record<ThemeMode, string>>(() => ({
  light: translate("common.theme.light"),
  dark: translate("common.theme.dark"),
  auto: translate("common.theme.auto")
}));
const shortLabels = computed<Record<ThemeMode, string>>(() => ({
  light: translate("common.theme.lightShort"),
  dark: translate("common.theme.darkShort"),
  auto: translate("common.theme.autoShort")
}));

const toggleTheme = () => {
  const nextIndex = (currentModeIndex.value + 1) % modes.length;
  theme.setTheme(modes[nextIndex]);
};

const selectMode = (mode: ThemeMode) => {
  theme.setTheme(mode);
};

const getIcon = (mode: ThemeMode) => {
  switch (mode) {
    case "light":
      return IconSun;
    case "dark":
      return IconMoon;
    case "auto":
      return IconAuto;
  }
};
</script>

<template>
  <div :class="containerClass">
    <template v-if="isSidebar">
      <div class="flex w-full items-center gap-2">
        <button
          v-for="mode in modes"
          :key="mode"
          type="button"
          class="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border px-2 py-2 text-xs font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 focus-visible:ring-offset-0"
          :class="
            currentMode === mode
              ? 'border-emerald-400/70 bg-emerald-500/15 text-emerald-600 dark:border-emerald-500/70 dark:bg-emerald-500/20 dark:text-emerald-300 shadow-sm'
              : 'border-transparent bg-transparent text-gray-600 hover:bg-gray-100/70 dark:text-slate-300 dark:hover:bg-slate-700/70'
          "
          @click="selectMode(mode)"
        >
          <component
            :is="getIcon(mode)"
            :class="[
              'h-4 w-4',
              currentMode === mode ? 'text-emerald-600 dark:text-emerald-300' : 'text-gray-500 dark:text-slate-300'
            ]"
          />
          <span
            :class="
              currentMode === mode ? 'text-emerald-600 dark:text-emerald-300' : 'text-gray-600 dark:text-slate-300'
            "
          >
            {{ shortLabels[mode] }}
          </span>
        </button>
      </div>
    </template>
    <template v-else>
      <button
        class="inline-flex items-center justify-center h-10 w-10 rounded-lg text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
        :title="modeLabels[currentMode]"
        @click="toggleTheme"
      >
        <component :is="getIcon(currentMode)" class="h-5 w-5" />
      </button>
    </template>
  </div>
</template>
